// organize-imports-ignore

import "./allowExternalDomManipulators";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router";
import { ErrorCatcher } from "./common/error-catcher";
import { configureStore } from "./store";
import { AppNavigateBridge } from "./utils/app-navigate";
import { lazyWithRetry } from "./utils/lazy";
import { NavigationGuardProvider } from "./utils/navigation-guard";
import "./style/tailwind.css";

const App = lazyWithRetry(() => import("./App"));
const LoginEntry = lazyWithRetry(() => import("./LoginEntry"));
const MobileEntry = lazyWithRetry(() => import("./MobileEntry"));
const BranchLinkEntry = lazyWithRetry(() => import("./BranchLinkEntry"));
const ThreeDSCallbackEntry = lazyWithRetry(
  () => import("./ThreeDSCallbackEntry")
);

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);

  // Declarative Mode (<BrowserRouter> + JSX <Routes>/<Route>), not Data or
  // Framework Mode — pure client-rendered SPA, no SSR. SSR later means
  // adopting Framework Mode specifically, a separate scoped migration.
  //
  // Every branch below gets its own <BrowserRouter>: exactly one branch
  // mounts per page load, so there's never more than one active. Standalone
  // entries (login/mobile/branch/3ds-callback) don't run the main
  // App/Routes tree, but still render shared components (e.g. Button) that
  // call react-router hooks unconditionally — those throw without a Router
  // ancestor, so every entry needs one.
  //
  // <BrowserRouter> wraps <ErrorCatcher>, not the reverse: ErrorCatcher's
  // fallback UI renders <Button>, which also needs a Router ancestor. If
  // ErrorCatcher were outermost, an error below would unmount the Router
  // too, so the fallback's own Button would throw with nothing left to
  // catch it — blank page instead of the fallback.
  //
  // <NavigationGuardProvider> sits inside <BrowserRouter> for the same
  // reason: <Button> also calls useNavigationGuard() unconditionally.
  if (window.location.pathname.startsWith("/login")) {
    root.render(
      // <React.StrictMode>
      <BrowserRouter>
        <NavigationGuardProvider>
          <ErrorCatcher>
            <React.Suspense fallback={null}>
              <LoginEntry />
            </React.Suspense>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  } else if (window.location.pathname.startsWith("/mobile")) {
    root.render(
      // <React.StrictMode>
      <BrowserRouter>
        <NavigationGuardProvider>
          <ErrorCatcher>
            <React.Suspense fallback={null}>
              <MobileEntry />
            </React.Suspense>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  } else if (window.location.pathname.startsWith("/branch")) {
    root.render(
      // <React.StrictMode>
      <BrowserRouter>
        <NavigationGuardProvider>
          <ErrorCatcher>
            <React.Suspense fallback={null}>
              <BranchLinkEntry />
            </React.Suspense>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  } else if (window.location.pathname.startsWith("/3ds-callback")) {
    root.render(
      // <React.StrictMode>
      <BrowserRouter>
        <NavigationGuardProvider>
          <ErrorCatcher>
            <React.Suspense fallback={null}>
              <ThreeDSCallbackEntry />
            </React.Suspense>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  } else {
    const store = configureStore();

    root.render(
      // <React.StrictMode>
      // <BrowserRouter> outermost, same reasoning as the standalone entries
      // above: it must wrap <ErrorCatcher>, not sit inside it, so the
      // fallback UI's <Button> (which calls useLocation()) still has a
      // Router ancestor when something below actually crashes.
      <BrowserRouter>
        <NavigationGuardProvider>
          {/* Only needed here, not the standalone entries below - its two
              current callers (websocket, page-and-routing helpers) are only
              reachable from the main App tree. See src/utils/app-navigate.tsx. */}
          <AppNavigateBridge />
          <ErrorCatcher showHomeLink>
            {/* stabilityCheck="always" (react-redux default is "once", dev-only
              either way, no-op in production builds): makes react-redux warn
              in the console whenever a useSelector call returns a new
              reference for identical state - exactly the
              `useSelector((state) => ({...}))` combined-object-literal
              pattern that silently defeated several components' React.memo
              by making them re-render on every dispatch app-wide (see
              perf/selector-cascade-and-list-item-fixes). "once" only samples
              each selector's first call; "always" catches it regardless of
              when it starts happening. */}
            <Provider store={store} stabilityCheck="always">
              <React.Suspense fallback={null}>
                <App />
              </React.Suspense>
            </Provider>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  }
}
